import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, Department} from 'yougile-model'
import {Avatar, AvatarGroup} from '@components/common/base/avatar'
import {Badge} from '@components/common/base/badge'
import {Icon} from '@components/common/base/icon'
import {BgOpacityContainer} from '@components/common/bg-opacity-container/bg-opacity-container'
import {DivWithHint} from '@components/common/hint/hint'
import {useBoolean} from '@containers/hooks'
import {DragControl, dragPropsFromCtl} from '@containers/hooks-draggable'
import IconArrow from './icon-arrow.svg'
import IconDragProject from './icon-drag.svg'

const TwItem = tw.div`
  relative
`

const TwItemCnt = tw.div<{$dragged: boolean}>`
  ${p => (p.$dragged ? 'opacity-30' : '')}
  bg-grey-l80
  cursor-pointer
  h-40
  py-6
  pr-16
  pl-4
  rounded-4
  relative
  flex
  items-center
  hover:bg-interactive-l100
  w-full
`

const TwChooseUser = tw.div`
  flex
  items-center
  justify-center
  w-16
  h-16
  rounded-full
  cursor-pointer
  bg-grey-l60
`

const MultiUsers = (props: {users: DataObjectId[]; maxShow?: number}) => {
  const {users, maxShow = 5} = props
  const hasMore = users.length > maxShow
  return (
    <div className="flex gap-4">
      {users.length > 0 ? (
        users
          .slice(0, maxShow - (hasMore ? 1 : 0))
          .map(user => (
            <Avatar key={user} userId={user} size={16} hideOutline hideHint />
          ))
      ) : (
        <TwChooseUser>
          <Icon icon="IconUser" size={16} />
        </TwChooseUser>
      )}
      {hasMore && (
        <Badge type="rounded" size={16}>{`+${
          users.length - maxShow + 1
        }`}</Badge>
      )}
    </div>
  )
}

const UsersGroup = (props: {users: DataObjectId[]}) => {
  const {users} = props
  if (users.length > 0) {
    return (
      <AvatarGroup
        userIds={users}
        size={16}
        outlineColor={'grey-l100'}
        hideStatus
      />
    )
  }
  return (
    <TwChooseUser>
      <Icon icon="IconUser" size={16} />
    </TwChooseUser>
  )
}

interface DepartmentProps {
  department: Department
  all: Department[]
  allUsers: DataObjectId[]
  under: DataObjectId
  dragCtl: DragControl<DataObjectId | 'top'>
  isAtEnd: boolean
  canEdit: boolean
  click: (id: DataObjectId) => () => void
  fromFilter?: boolean
}

export const DepartmentItem = (props: DepartmentProps) => {
  const {department, under, fromFilter} = props
  if (department.parentId !== under && !fromFilter) {
    return null
  }
  return (
    <TwItem>
      <div className="relative text-secondary">
        {!!under && <IconArrow className="m-12 absolute -left-40 " />}
        <ItemCnt {...props} />
      </div>
      <ShowChildren {...props} />
    </TwItem>
  )
}

const TwShowChildren = tw.div`
  flex 
  flex-col 
  gap-8 
  mt-8 
  pl-40
`

const ShowChildren = (props: DepartmentProps) => {
  const {department, all, dragCtl, click, canEdit, allUsers, fromFilter} = props
  const {id} = department
  const children = all.filter(d => d.parentId === id)
  const showChildren = !fromFilter && children.length > 0
  if (!showChildren) {
    return null
  }
  return (
    <TwShowChildren>
      {children.map((d, ix) => (
        <DepartmentItem
          key={d.id}
          canEdit={canEdit}
          department={d}
          all={all}
          under={id}
          dragCtl={dragCtl}
          isAtEnd={ix === children.length - 1}
          click={click}
          allUsers={allUsers}
        />
      ))}
    </TwShowChildren>
  )
}

const TwBgOpacityContainer = tw.div`
  absolute 
  top-2 
  rounded-4 
  left-2 
  right-2 
  bottom-2 
  border 
  border-dashed 
  border-grey-l60
  text-primary 
  pointer-events-none
`

const TwDivider = tw.div`
  w-1
  h-16
  mx-4
  bg-grey-l60
`

const TwDepartmentTitle = tw.div`
  flex-auto
  truncate
  text-14
  text-primary
  ml-18
`

const TwIconDrag = tw.div`
  flex 
  text-disabled 
  absolute
`

const ItemCnt = observer((props: DepartmentProps) => {
  const {department, dragCtl, click, canEdit, allUsers} = props
  const {id} = department
  const isDragOver = dragCtl.dragOverId === id && dragCtl.draggedId !== id
  const dragProps = dragPropsFromCtl(id, dragCtl)
  const hover = useBoolean(false)
  const managers = department.managers.filter(uid => allUsers.includes(uid))
  const members = department.members.filter(uid => allUsers.includes(uid))
  return (
    <TwItemCnt
      $dragged={dragCtl.draggedId === id}
      draggable={canEdit}
      onClick={click(id)}
      onMouseEnter={() => canEdit && hover.turnOn()}
      onMouseLeave={() => canEdit && hover.turnOff()}
      {...(canEdit ? dragProps : {})}
    >
      {canEdit && hover.value && (
        <TwIconDrag>
          <IconDragProject />
        </TwIconDrag>
      )}
      <TwDepartmentTitle>{department.title}</TwDepartmentTitle>
      <DivWithHint content={Lang.msg.companies.depManagers}>
        <MultiUsers users={managers} />
      </DivWithHint>
      <TwDivider />
      <DivWithHint content={Lang.msg.companies.depMembers}>
        <UsersGroup users={members} />
      </DivWithHint>
      {isDragOver && (
        <TwBgOpacityContainer>
          <BgOpacityContainer
            bgColorClassName="bg-grey-l80 rounded-4 absolute"
            bgOpacityClassName="opacity-40"
            className="h-full w-full"
          >
            <></>
          </BgOpacityContainer>
        </TwBgOpacityContainer>
      )}
    </TwItemCnt>
  )
})
